import React, { useCallback } from 'react'

import { useConnectModal } from '@rainbow-me/rainbowkit'
import { useDisconnect } from 'wagmi'

import { WaitingForMoreSignaturesTooltip } from '~/components/smart-accounts/WaitingForMoreSignaturesTooltip'
import { useMissingSignatures } from '~/components/smart-accounts/useMissingSignatures'
import { ApproveAndSubmitTransactionParams } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { useTransactionProperties } from '~/hooks/useTransactions'
import {
  BackendTransaction,
  BackendSmartAccount,
  TransactionStatus,
} from '~/types'
import { isTransactionOrg } from '~/utils/smartAccounts'

/**
 * Hook that encapsulates all the logic for approving/submitting transactions
 * Used by both ApproveTransactionButton component and keyboard shortcuts
 */
export const useApproveTransactionAction = ({
  transaction,
  smartAccount,
  hasAccessOverride,
  isProcessing,
  approveAndSubmitTransaction,
  onClose,
  onSign,
  onConfirmation,
  onSubmitError,
}: {
  transaction: BackendTransaction
  smartAccount?: BackendSmartAccount
  hasAccessOverride?: boolean
  isProcessing?: boolean
  approveAndSubmitTransaction?: (
    params: ApproveAndSubmitTransactionParams,
  ) => void
  onClose?: () => void
  onSign?: (status: TransactionStatus) => void
  onConfirmation?: () => void
  onSubmitError?: (e: unknown) => void
}) => {
  const { openConnectModal } = useConnectModal()
  const { disconnect } = useDisconnect()
  const isOrgTransaction = transaction && isTransactionOrg(transaction)

  const {
    canConnect,
    canExecute,
    canSign,
    canSubmit,
    hasSigned,
    isApproved,
    hasAccess,
    isSigning,
    isSubmitting,
    isConfirming,
    isUpdatingGas,
  } = useTransactionProperties(transaction, hasAccessOverride)

  const numMissingSignatures = useMissingSignatures(transaction, smartAccount)

  const triggerApproval = useCallback(() => {
    const canAttemptSign =
      (isOrgTransaction || hasAccessOverride) && transaction && smartAccount
    if (canAttemptSign && approveAndSubmitTransaction) {
      approveAndSubmitTransaction({
        transaction,
        smartAccount,
        onSign,
        onSubmit: () => {
          onClose?.()
        },
        onConfirmation,
        onSubmitError,
      })
    }
  }, [
    approveAndSubmitTransaction,
    transaction,
    smartAccount,
    isOrgTransaction,
    hasAccessOverride,
    onClose,
    onSign,
    onConfirmation,
    onSubmitError,
  ])

  const onSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation()
      triggerApproval()
    },
    [triggerApproval],
  )

  const isDisabled = !canSign && !canSubmit && !canExecute && !canConnect

  // Determine the button state and action
  if (!hasAccess) {
    return {
      type: 'disabled' as const,
      onClick: () => {},
      triggerApproval,
      isDisabled: true,
      disabledReason: !hasAccess
        ? "You don't have access to this transaction"
        : undefined,
      text: 'Sign',
    }
  }

  if (hasSigned && isDisabled && smartAccount && numMissingSignatures > 0) {
    return {
      type: 'disabled' as const,
      onClick: () => {},
      triggerApproval,
      isDisabled: true,
      disabledReason: (
        <WaitingForMoreSignaturesTooltip
          numMissingSignatures={numMissingSignatures}
          smartAccount={smartAccount}
          transaction={transaction}
        />
      ),
      text: 'Sign',
    }
  }

  // Only prompt to connect a wallet for EOA signing when the user has no other
  // way to sign. Passkey-only users on mixed accounts (passkey + EOA signers)
  // must still see "Sign", not "Connect" — `canSign` covers both passkey and
  // EOA paths, so gating on `!canSign` preserves the original ROOT-account
  // flow (no passkey signers ⇒ canSign false ⇒ Connect) while letting passkey
  // holders sign directly.
  if (smartAccount && smartAccount.eoaSigners.length > 0) {
    if (canConnect && !canSign) {
      return {
        type: 'action' as const,
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          disconnect()
          openConnectModal?.()
        },
        triggerApproval,
        text: 'Connect',
      }
    }
  }

  // Determine action type
  const actionType = canExecute ? 'execute' : canSubmit ? 'submit' : 'sign'

  // Action properties map
  const actions = {
    // consolidated "Execute" and "Submit" button text to simplify UX
    execute: { text: 'Submit', eventName: 'clickedExecuteTransaction' },
    submit: { text: 'Submit', eventName: 'clickedSubmitTransaction' },
    sign: { text: 'Sign', eventName: 'clickedSignTransaction' },
  }

  // Get disabled reason if applicable
  const disabledReason = isDisabled
    ? hasSigned
      ? 'You have already signed this transaction'
      : isApproved && !canSubmit
        ? 'You must be a member of the workspace and logged in to submit this transaction'
        : !canSign && smartAccount && smartAccount.eoaSigners.length > 0
          ? 'The connected wallet is not a signer on this account'
          : !canSign
            ? 'You are not a signer on this account'
            : undefined
    : undefined

  return {
    type: 'action' as const,
    onClick: onSubmit,
    triggerApproval,
    isLoading:
      isSigning ||
      isSubmitting ||
      isConfirming ||
      isUpdatingGas ||
      isProcessing,
    isDisabled: isDisabled,
    disabledReason,
    text:
      isApproved && !canSign && !canSubmit
        ? 'Submit'
        : actions[actionType].text,
    eventName: actions[actionType].eventName,
    eventProperties: { transactionId: transaction.id },
  }
}
